import axios from 'axios';
import { useCallback, useState } from 'react';

import { AuthenticationClient } from '../../authentication/client';
import { useLogging } from '../../logging';
import { ErrorType, PageView } from '../types';
import { getPageView } from './helpers';
import constants from '../constants';

const { SHARE_VIEW_API_URL } = constants;

export function useGetPageView<TViewState extends object>(): {
  execute: (id: string) => Promise<void>;
  pageView: PageView<TViewState>;
  loading: boolean;
  error: ErrorType;
} {
  const { error } = useLogging();
  const [pageView, setPageView] = useState<PageView<TViewState>>(undefined);
  const [loading, setLoading] = useState(false);
  const [errorType, setErrorType] = useState<ErrorType>(ErrorType.NO_ERROR);

  const execute = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true);
      try {
        const pageView = await getPageView<TViewState>(id);
        setPageView(pageView);
        setErrorType(ErrorType.NO_ERROR);
      } catch (e) {
        error(e, { message: 'Error while getting saved view' });
        setPageView(null);
        setErrorType(ErrorType.FAILED_TO_GET);
      } finally {
        setLoading(false);
      }
    },
    [error]
  );

  return {
    execute,
    pageView,
    loading,
    error: errorType,
  };
}

export type CreatePageView<TViewState extends object> = Omit<PageView<TViewState>, 'key' | 'created_at'>;

export function useCreatePageView<TViewState extends object>(): {
  execute: (pageView: CreatePageView<TViewState>) => Promise<string>;
  result: string;
  saving: boolean;
  error: ErrorType;
} {
  const { error } = useLogging();
  const [result, setResult] = useState<string>(undefined);
  const [saving, setSaving] = useState(false);
  const [errorType, setErrorType] = useState<ErrorType>(ErrorType.NO_ERROR);

  const execute: (pageView: CreatePageView<TViewState>) => Promise<string> = async (
    pageView: CreatePageView<TViewState>
  ): Promise<string> => {
    setSaving(true);
    try {
      const token = await AuthenticationClient.getInstance().getAccessToken();
      const response = await axios.post<PageView<TViewState>>(`${SHARE_VIEW_API_URL}/v1/page-views`, pageView, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const pageViewResponse = response.data;

      if (!pageViewResponse.key?.length) {
        throw Error('Failed to save view');
      }

      setResult(pageViewResponse.key);
      setErrorType(ErrorType.NO_ERROR);

      return pageViewResponse.key;
    } catch (e) {
      error(e, { message: 'Error while saving view' });
      setResult(null);
      setErrorType(ErrorType.UNABLE_TO_SAVE);
    } finally {
      setSaving(false);
    }
  };

  return {
    execute,
    result,
    saving,
    error: errorType,
  };
}
